import React, { Suspense, lazy, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Footer from './components/Footer';
import Loader from './components/Loader';
import { backgroundGlow } from './utils/animationVariants';

// Lazy load sections for better initial bundle size and performance
const About = lazy(() => import('./components/About'));
const SystemsSection = lazy(() => import('./components/SystemsSection'));
const Projects = lazy(() => import('./components/Projects'));
const Contact = lazy(() => import('./components/Contact'));

// Simple loading fallback
const SectionLoader = () => (
  <div className="w-full h-40 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
  </div>
);

function App() {
  const [isAppLoaded, setIsAppLoaded] = useState(false);

  // Check if we are on mobile to disable heavy background animations
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <>
      <AnimatePresence mode="wait">
        {!isAppLoaded && (
          <Loader onLoadingComplete={() => setIsAppLoaded(true)} />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isAppLoaded ? 1 : 0 }}
        transition={{ duration: 1, ease: "easeInOut" }}
        className="relative min-h-screen w-full bg-background-dark text-text-primary-dark selection:bg-gold/30 overflow-x-hidden"
      >
        {/* Background Effects - Optimized */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {!isMobile && (
          <>
            <motion.div
              {...backgroundGlow}
              className="premium-blur top-[-10%] left-[-10%] w-[500px] h-[500px] bg-gold/10"
              style={{ willChange: 'opacity' }}
            />
            <motion.div
              {...backgroundGlow}
              transition={{ ...backgroundGlow.animate.transition, delay: 2 }}
              className="premium-blur bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-gold/5"
              style={{ willChange: 'opacity' }}
            />
          </>
        )}
        <div
          className="absolute inset-0 opacity-[0.02] pointer-events-none mix-blend-overlay"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }}
        />
      </div>

      <div className="relative z-10 flex flex-col w-full overflow-visible">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <Navbar />
          <main className="flex flex-col gap-32 py-20">
            <Hero />
            <Suspense fallback={<SectionLoader />}>
              <About />
              <SystemsSection />
              <Projects />
              <Contact />
            </Suspense>
          </main>
          <Footer />
        </div>
      </div>
      </motion.div>
    </>
  );
}

export default App;
